import ErrorBoundary from '@/components/ErrorBoundary';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { useCompanyValues } from '@/providers/CompanyProvider';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useState } from 'react';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { showToast } from '@/features/toasts/toastsSlice';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import { ToastType } from '@/models/toast';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { RouteConstants } from '@/features/router/RouteConstants';
import { EMAIL_TEMPLATE_CONNECTOR_FLOW_NAME } from '@/features/campaigns/EmailCampaign/constants';

const AUDIENCE_ID = 'a-JXX5hb38ZWJnKidR';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

export const EmailTemplateCreation = () => {
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const [createFlowInstance] = useCreateFlowInstanceMutation();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const history = useHistory();

  const { data: connectorInstances, isLoading: isConnectorInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const { data: connectorFlows, isLoading: isConnectorFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId, queryParams: { showUserJourney: true } },
    { skip: !workspaceId }
  );

  const emailConnectorFlow = connectorFlows?.find((flow) => flow.name === EMAIL_TEMPLATE_CONNECTOR_FLOW_NAME);
  const emailConnectorFlowId = emailConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === emailConnectorFlow?.connectorId)?.id;

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);

  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: emailConnectorFlowId,
        audienceId: AUDIENCE_ID,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);

      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      setPrepareResult(prepareResultResponse as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!emailConnectorFlowId) {
      void fetchPrepareData();
    }
  }, [connectorInstanceId, emailConnectorFlowId, prepareStep, currentLanguage, workspaceId]);

  type CreateTemplateOpts = {
    flowParams?: FlowFormData;
    flowMappings?: FlowFormData;
    fromFlowComplete?: boolean;
  };

  const handleCreateTemplate = async (opts?: CreateTemplateOpts) => {
    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    const mappingsUiRequired = !!multiStepMappingsConfig?.schema?.properties && Object.keys(multiStepMappingsConfig.schema.properties).length > 0;
    if (!opts?.fromFlowComplete && mappingsUiRequired && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    const instance = instances?.find((inst) => inst.connectorId === emailConnectorFlow?.connectorId);
    if (!instance) {
      const message = String(t('noConnectorInstanceFound'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }
    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: emailConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId: AUDIENCE_ID,
      flowParams,
      flowMappings,
    });
    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(
        showToast({
          title: 'Template created successfully',
          type: ToastType.SUCCESS,
        })
      );
      appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
      history.push(RouteConstants.emailTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('flowInstanceCreationFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON:cancel' as any,
    onClick: () => {},
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON:confirm' as any,
    onClick: () => void handleCreateTemplate(),
    disabled: !isMappingsFormValid,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const multiStepFlowFormProps = {
    showTemplatePreview: false,
    audienceId: AUDIENCE_ID,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: emailConnectorFlowId!,
    paramsConfig: prepareResult || {},
    onComplete: (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
      setFormData(allParams);
      setMultiStepMappingsConfig(mappingsConfig);
      setMappingData({});
      setIsMappingsFormValid(false);
      const hasMappingsUi = !!mappingsConfig?.schema?.properties && Object.keys(mappingsConfig.schema.properties).length > 0;
      if (!hasMappingsUi) {
        void handleCreateTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
      }
    },
  };

  const isTemplateLoading = isConnectorFlowsLoading || isConnectorInstancesLoading || !prepareResult;

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('emailWorkspace.templates.createEmailTemplate')}</h2>
      </div>
      <ErrorBoundary>
        {prepareResult ? (
          <MultiStepFlowForm {...multiStepFlowFormProps} />
        ) : (
          <p className='text-sm text-gray-600'>
            {isTemplateLoading ? t('emailWorkspace.templates.templateLoadingMessage') : t('emailWorkspace.templates.failedToLoadTemplate')}
          </p>
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        {(() => {
          if (!multiStepMappingsConfig?.schema?.properties || Object.keys(multiStepMappingsConfig?.schema.properties).length === 0) {
            return null;
          }
          return (
            <div>
              <h3 className='mb-4 text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
              <FlowMappingsForm
                config={multiStepMappingsConfig}
                formData={mappingData}
                onFormDataChange={handleMappingDataChange}
                onValidationChange={handleMappingsValidationChange}
              />
              <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
            </div>
          );
        })()}
      </ErrorBoundary>
    </div>
  );
};
